import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone === true;

  useEffect(() => {
    // Don't show if already installed or dismissed this session
    if (isStandalone || sessionStorage.getItem('install_dismissed')) return;

    if (isIOS) {
      // iOS: show manual guide after short delay
      const timer = setTimeout(() => setShowIOSGuide(true), 2000);
      return () => clearTimeout(timer);
    }

    // Android/Chrome: capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Hide if app gets installed
    window.addEventListener('appinstalled', () => {
      setShowInstall(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [isStandalone, isIOS]);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstall(false);
    }
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  const handleDismiss = () => {
    setDismissed(true);
    setShowInstall(false);
    setShowIOSGuide(false);
    sessionStorage.setItem('install_dismissed', 'true');
  };

  if (dismissed || isStandalone) return null;

  // Android/Chrome install banner
  if (showInstall) {
    return (
      <div 
        data-testid="install-prompt-banner"
        className="fixed bottom-0 left-0 right-0 z-50 animate-in slide-in-from-bottom duration-500"
      >
        <div className="mx-3 mb-3 rounded-2xl overflow-hidden shadow-2xl border border-emerald-200/50"
          style={{ background: 'linear-gradient(135deg, #065f46 0%, #047857 50%, #059669 100%)' }}
        >
          <button 
            onClick={handleDismiss}
            data-testid="install-prompt-dismiss"
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          <div className="px-5 pt-5 pb-4">
            <p className="text-emerald-100 text-xs font-medium tracking-wider uppercase mb-1">
              Nzadi Fit
            </p>
            <h3 className="text-white text-lg font-bold leading-snug">
              Controle as suas calorias e atinja o corpo dos seus sonhos
            </h3>
          </div>

          <div className="px-5 pb-5">
            <button 
              onClick={handleInstall}
              data-testid="install-prompt-button"
              className="w-full py-3.5 rounded-xl font-bold text-base transition-all active:scale-[0.97]"
              style={{ 
                background: 'white', 
                color: '#065f46',
                boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
              }}
            >
              Instalar App Grátis
            </button>
          </div>
        </div>
      </div>
    );
  }

  // iOS manual install guide
  if (showIOSGuide) {
    return (
      <div 
        data-testid="ios-install-guide"
        className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 animate-in fade-in duration-300"
        onClick={handleDismiss}
      >
        <div 
          className="w-full max-w-md mx-3 mb-3 rounded-2xl bg-white shadow-2xl animate-in slide-in-from-bottom duration-500"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-emerald-700 text-xs font-semibold tracking-wider uppercase mb-1">
                  Nzadi Fit
                </p>
                <h3 className="text-gray-900 text-lg font-bold leading-snug">
                  Controle as suas calorias e atinja o corpo dos seus sonhos
                </h3>
              </div>
              <button 
                onClick={handleDismiss}
                data-testid="ios-guide-dismiss"
                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors -mt-1"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="px-5 pb-5 space-y-4">
            <p className="text-gray-600 text-sm">
              Instale a app no seu iPhone em 2 passos:
            </p>

            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">
                  1
                </div>
                <div className="text-sm">
                  <span className="font-semibold text-gray-900">Toque em </span>
                  <span className="inline-flex items-center bg-white px-2 py-0.5 rounded border border-gray-200">
                    <svg className="w-4 h-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    <span className="ml-1 text-gray-700 text-xs font-medium">Partilhar</span>
                  </span>
                  <span className="font-semibold text-gray-900"> no Safari</span>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-50">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-sm">
                  2
                </div>
                <div className="text-sm">
                  <span className="font-semibold text-gray-900">Toque em </span>
                  <span className="inline-flex items-center bg-white px-2 py-0.5 rounded border border-gray-200">
                    <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="ml-1 text-gray-700 text-xs font-medium">Adicionar ao Ecrã</span>
                  </span>
                </div>
              </div>
            </div>

            <button 
              onClick={handleDismiss}
              data-testid="ios-guide-ok-button"
              className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all active:scale-[0.97]"
              style={{ background: 'linear-gradient(135deg, #065f46, #059669)' }}
            >
              Entendi!
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPrompt;
